/**
 * SplashLogin — animated PARI splash that slides backwards and merges into a
 * login card. Provides login, forgot-password (OTP currently bypassed) and a
 * lightweight register flow. Calls onAuthed(token, user) on success.
 */
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, LogIn, ArrowLeft, KeyRound, UserPlus, ShieldCheck } from "lucide-react";
import "./SplashLogin.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


export default function SplashLogin({ onAuthed }) {
  const [phase, setPhase] = React.useState("intro");          // intro | form
  const [view, setView] = React.useState("login");            // login | forgot | register
  React.useEffect(() => {
    const t = setTimeout(() => setPhase("form"), 1600);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`pari-auth ${phase}`}>
      {/* Left: logo + product copy — starts centered, slides to the left */}
      <div className="pari-left">
        <div className="pari-left__inner">
          <img className="pari-logo" src="/assets/app-logo/logo.png" alt="pycrop logo"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="pari-copy">
            <h2 className="pari-copy__title">Crop insurance Pricing & Payout workspace</h2>
            <p className="pari-copy__text">
              Pycrop is an end-to-end workspace for crop insurance product's
              pricing, payout, and analytics. Both, RWBCIS and PMFBY are enabled on this one platform — from
              data preparation to burning cost to payout calculations, and
              downloadable and auditable rate workbooks. All with extra-ordinary visualisation aids.
            </p>
            <ul className="pari-copy__list">
              {/* <li>AI data preparation — collate PDFs, scans, Excel & CSV into one standard format</li>
              <li>Threshold-yield methods, historical & detrending burning cost, clustering</li>
              <li>District-level APR heat maps, validations and exportable summaries</li>
              <li>AI enabled term sheet data extraction - collates all index and parameters</li> */}
            </ul>
            {/* Pixel-art march of progress; the loop lives inside the SVG */}
            <figure className="pari-evolution">
              <img src="/assets/pyri-evolution.svg"
                alt="Five pixel-art figures on one ground line: monkey, ape, early human with a spear, farmer with a hoe, modern actuary holding a tablet showing the pycrop pi"
                draggable="false"
                onError={(e) => {
                  // Retry once before giving up: a single failed fetch (dev-server
                  // hiccup, aborted navigation) otherwise hid the figure for the
                  // whole session, which reads exactly like the loop not running.
                  const img = e.currentTarget;
                  if (img.dataset.retried) { img.parentElement.style.display = "none"; return; }
                  img.dataset.retried = "1";
                  img.src = `/assets/pyri-evolution.svg?r=${Date.now()}`;
                }} />
              <figcaption>The Evolution of Insurance Tools</figcaption>
            </figure>
          </div>
        </div>
      </div>

      {/* Right: sign-in card — appears after the brand slides left */}
      <div className="pari-right">
        <div className="pari-card">
          {view === "login" && <LoginView onAuthed={onAuthed} setView={setView} />}
          {view === "forgot" && <ForgotView setView={setView} />}
          {view === "register" && <RegisterView onAuthed={onAuthed} setView={setView} />}
        </div>
      </div>
    </div>
  );
}

function Header({ title, subtitle }) {
  return (
    <div className="mb-5 text-center">
      <h1 className="text-xl font-bold text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function LoginView({ onAuthed, setView }) {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!username.trim() || !password) { toast.error("Enter username and password"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { username: username.trim(), password });
      onAuthed(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name || r.data.user.username}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="pari-fadein space-y-4">
      <Header title="Sign in" subtitle="Access your pycrop workspace" />
      <div className="space-y-1.5">
        <Label>Username</Label>
        <Input autoFocus value={username} onChange={(e) => setUsername(e.target.value)} placeholder="demo" />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label>Password</Label>
          <button type="button" onClick={() => setView("forgot")}
            className="text-xs text-emerald-700 hover:underline">Forgot password?</button>
        </div>
        <div className="relative">
          <Input type={show ? "text" : "password"} value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          <button type="button" onClick={() => setShow((s) => !s)}
            className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 p-1">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />} Sign in
      </Button>
      <div className="text-center text-sm text-slate-500">
        New here?{" "}
        <button type="button" onClick={() => setView("register")} className="text-emerald-700 hover:underline font-medium">
          Create an account
        </button>
      </div>
      {/* <div className="text-center text-xs text-slate-400">Demo: <b>****</b> / <b>*****</b></div> */}
    </form>
  );
}

function RegisterView({ onAuthed, setView }) {
  const [f, setF] = React.useState({ username: "", name: "", password: "" });
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!f.username.trim() || f.password.length < 6) { toast.error("Username required, password ≥ 6 chars"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/register`, f);
      onAuthed(r.data.token, r.data.user);
      toast.success("Account created");
    } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="pari-fadein space-y-4">
      <Header title="Create account" subtitle="Set up your sign-in" />
      <div className="space-y-1.5"><Label>Username</Label>
        <Input autoFocus value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></div>
      <div className="space-y-1.5"><Label>Full name</Label>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="optional" /></div>
      <div className="space-y-1.5"><Label>Password</Label>
        <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
      <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />} Create account
      </Button>
      <button type="button" onClick={() => setView("login")}
        className="w-full text-sm text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
      </button>
    </form>
  );
}

function ForgotView({ setView }) {
  const [step, setStep] = React.useState("request");          // request | otp | reset
  const [username, setUsername] = React.useState("");
  const [otp, setOtp] = React.useState(["", "", "", "", "", ""]);
  const [pw, setPw] = React.useState({ a: "", b: "" });
  const [busy, setBusy] = React.useState(false);
  const [seconds, setSeconds] = React.useState(0);
  const inputs = React.useRef([]);

  React.useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const sendOtp = async () => {
    if (!username.trim()) { toast.error("Enter your username"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/forgot`, { username: username.trim() });
      toast.message(r.data.message || "If the account exists, you can proceed.");
      setStep("otp"); setSeconds(30);
    } catch { toast.error("Could not start password reset"); }
    finally { setBusy(false); }
  };

  const onOtpChange = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...otp]; next[i] = v; setOtp(next);
    if (v && i < 5) inputs.current[i + 1]?.focus();
  };

  const verify = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { username: username.trim(), otp: otp.join("") });
      setStep("reset");
    } catch (e) { toast.error(e.response?.data?.detail || "OTP verification failed"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (pw.a.length < 6) { toast.error("Password must be ≥ 6 chars"); return; }
    if (pw.a !== pw.b) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { username: username.trim(), otp: otp.join(""), new_password: pw.a });
      toast.success("Password updated — sign in");
      setView("login");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="pari-fadein space-y-4">
      <Header title="Reset password"
        subtitle={step === "request" ? "We'll guide you through resetting it"
          : step === "otp" ? "Enter the verification code" : "Choose a new password"} />

      <div className="flex items-center gap-2 rounded-lg bg-amber-50 text-amber-800 px-3 py-2 text-xs">
        <ShieldCheck className="h-4 w-4 shrink-0" /> OTP verification is currently disabled — you can proceed without a code.
      </div>

      {step === "request" && (
        <>
          <div className="space-y-1.5"><Label>Username</Label>
            <Input autoFocus value={username} onChange={(e) => setUsername(e.target.value)} /></div>
          <Button onClick={sendOtp} disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />} Send OTP
          </Button>
        </>
      )}

      {step === "otp" && (
        <>
          <div className="pari-otp">
            {otp.map((d, i) => (
              <input key={i} ref={(el) => (inputs.current[i] = el)} value={d} inputMode="numeric" maxLength={1}
                onChange={(e) => onOtpChange(i, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Backspace" && !otp[i] && i > 0) inputs.current[i - 1]?.focus(); }} />
            ))}
          </div>
          <div className="flex items-center justify-between text-sm">
            <button type="button" disabled={seconds > 0} onClick={sendOtp}
              className={`${seconds > 0 ? "text-slate-400" : "text-emerald-700 hover:underline"}`}>
              {seconds > 0 ? `Resend OTP in ${seconds}s` : "Resend OTP"}
            </button>
            <span className="text-slate-400 text-xs">code optional (bypass)</span>
          </div>
          <Button onClick={verify} disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Verify & continue
          </Button>
        </>
      )}

      {step === "reset" && (
        <>
          <div className="space-y-1.5"><Label>New password</Label>
            <Input type="password" autoFocus value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Confirm password</Label>
            <Input type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} /></div>
          <Button onClick={reset} disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Update password
          </Button>
        </>
      )}

      <button type="button" onClick={() => setView("login")}
        className="w-full text-sm text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
      </button>
    </div>
  );
}
